import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import AuthGuard from '../components/AuthGuard';

export default function WithdrawResult() {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ name?: string; accounts?: { mezichan?: number }; bank_info?: { name?: string; bank?: string; card?: string } } | null>(null);
  const [paymentChannelUrl, setPaymentChannelUrl] = useState<string>('');
  const [withdrawRecord, setWithdrawRecord] = useState<{ amount: number; order_no: string; created_at: string } | null>(null);

  useEffect(() => {
    api.get('/me').then(({ data }) => setUser(data)).catch(() => { });
    api.get('/home-links').then(({ data }) => {
      if (data && typeof data.payment_channel === 'string' && data.payment_channel.length) {
        setPaymentChannelUrl(data.payment_channel);
      }
    }).catch(() => { });
    api.get('/mezichan/withdraw').then(({ data }) => {
      if (data?.data) setWithdrawRecord(data.data);
    }).catch(() => { });
  }, []);

  const amount = withdrawRecord?.amount ?? user?.accounts?.mezichan ?? 0;
  const timeStr = withdrawRecord?.created_at ?? '';
  const orderNo = withdrawRecord?.order_no ?? '';

  return (
    <div
      className="max-w-md mx-auto min-h-screen pb-6"
      style={{
        backgroundImage: 'url(/images/jine_beijing.png)',
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <AuthGuard />

      {/* Top header */}
      <div className="relative z-1">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Header logo */}
      <div className="flex justify-center pt-6 pb-4">
        <img src="/images/jine_logo.png" alt="云数家园" className="h-8" />
      </div>

      {/* Main card */}
      <div className="mx-4">
        <div
          className="rounded-2xl px-5 pt-6 pb-5"
          style={{
            backgroundImage: 'url(/images/jine_kuang.png)',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Bank logo */}
          <div className="flex justify-center mb-3">
            <img src="/images/jine_zhongguo.png" alt="中国银行" className="h-12" />
          </div>

          {/* Amount */}
          <div className="text-center mb-1">
            <p className="text-sm text-gray-500">交易金额 (元)</p>
            <p className="text-3xl text-black mt-1">{amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-sm text-gray-500 mt-1">交易成功</p>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-300 my-4" />

          {/* Details */}
          <div className="space-y-3">
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">创建时间</span>
              <span className="text-sm text-gray-800">{timeStr}</span>
            </div>
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">提现账户</span>
              <span className="text-sm text-gray-800">{user?.bank_info?.name ?? user?.name ?? '—'}</span>
            </div>

            {/* Progress */}
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">处理进度</span>
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-[#c4161c]" />
                    <div className="w-[1px] h-8 border-l border-dashed border-gray-300" />
                  </div>
                  <span className="text-sm text-gray-800 -mt-0.5">提交银行处理</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <img src="/images/jine_chuli.png" alt="" className="w-5 h-5" />
                    <div className="w-[1px] h-8 border-l border-dashed border-gray-300" />
                  </div>
                  <span className="text-sm text-gray-800 -mt-0.5">银行处理中</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-gray-300" />
                    <div className="w-[1px] h-8 border-l border-dashed border-gray-300" />
                  </div>
                  <span className="text-sm text-gray-400 -mt-0.5">交易成功</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-gray-300" />
                  </div>
                  <span className="text-sm text-gray-400 -mt-0.5">银行入账成功</span>
                </div>
              </div>
            </div>

            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">进度说明</span>
              <span className="text-sm text-[#c4161c]">银行入账成功, 资金到账处理中</span>
            </div>
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">提现类型</span>
              <span className="text-sm text-gray-800">实时到账</span>
            </div>
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">银行卡号</span>
              <span className="text-sm text-gray-800">
                {user?.bank_info
                  ? `${user.bank_info.bank} (${user.bank_info.card?.replace(/^(.{4})(.*)(.{4})$/, (_, a, m, b) => a + '*'.repeat(m.length) + b) ?? '—'})`
                  : '—'}
              </span>
            </div>
            <div className="flex">
              <span className="text-sm text-gray-400 w-[80px] flex-shrink-0">订单编号</span>
              <span className="text-sm text-gray-800">{orderNo}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom button */}
      {/* <div className="px-4 mt-5">
        <button
          onClick={() => {
            if (paymentChannelUrl) {
              window.open(paymentChannelUrl, '_blank');
            }
          }}
          className="w-full relative"
        >
          <img src="/images/jine_dianji.png" alt="点击进入收银台通道支付" className="w-full" />
          <span className="text-white absolute left-1/2 top-1/2 -translate-x-[50%] -translate-y-[50%]">点击进入收银台通道支付</span>
        </button>
      </div> */}

    </div>
  );
}
